class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.config = {
      autorotate: this.dataset.autorotate === 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed) * 1000,
      moveTime: parseFloat(this.dataset.speed),
      space: 100,
    };

    this.onNextPrevClick = this.handleNextPrevClick.bind(this);
    this.startAutorotate = this.startAutorotate.bind(this);
    this.stopAutorotate = this.stopAutorotate.bind(this);
    this.onMarqueeResize = this.marqueeAnimation.bind(this);

    if (theme.config.mqlSmall) {
      this.initOnMobile();
    }
    else {
      this.initOnDesktop();
    }

    document.addEventListener('matchSmall', () => {
      this.unload();
      this.initOnMobile();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.initOnDesktop();
    });
  }

  initOnDesktop() {
    switch(this.dataset.layout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;

      default:
        this.initMarqueeOptimized();
    }
  }

  initOnMobile() {
    switch(this.dataset.mobileLayout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;
    }
  }

  unload() {
    this.stopAutorotate();
    this.removeNextPrevListeners();
    this.classList.remove('is-carousel-ready');

    if (this.slides && this.slides.length) {
      this.slides.forEach((slide) => {
        slide.classList.remove('is-active');
        slide.removeAttribute('aria-hidden');
      });
    }

    if (this.classList.contains('marquee')) {
      this.classList.remove('marquee');
      this.querySelectorAll('.announcement-slider[aria-hidden]').forEach((slider) => {
        slider.remove();
      });
    }

    window.removeEventListener('resize', this.onMarqueeResize);

    if (this.observer && typeof this.observer.disconnect === 'function') {
      this.observer.disconnect();
    }
  }

  initCarousel() {
    if (this.dataset.blockCount == 1) {
      return;
    }

    this.slides = Array.from(this.querySelectorAll('.announcement-slider__slide'));
    this.currentIndex = 0;
    this.classList.add('is-carousel-ready');
    this.setActiveSlide(this.currentIndex);

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.prevButton && this.prevButton.addEventListener('click', this.onNextPrevClick);
    this.nextButton && this.nextButton.addEventListener('click', this.onNextPrevClick);

    this.addEventListener('mouseenter', this.stopAutorotate);
    this.addEventListener('mouseleave', this.startAutorotate);
    this.addEventListener('focusin', this.stopAutorotate);
    this.addEventListener('focusout', this.startAutorotate);

    this.startAutorotate();
  }

  removeNextPrevListeners() {
    this.prevButton && this.prevButton.removeEventListener('click', this.onNextPrevClick);
    this.nextButton && this.nextButton.removeEventListener('click', this.onNextPrevClick);
    this.removeEventListener('mouseenter', this.stopAutorotate);
    this.removeEventListener('mouseleave', this.startAutorotate);
    this.removeEventListener('focusin', this.stopAutorotate);
    this.removeEventListener('focusout', this.startAutorotate);
  }

  handleNextPrevClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    if (target.name === 'next') {
      this.goToSlide(this.currentIndex + 1);
    }
    else if (target.name === 'previous') {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  setActiveSlide(index) {
    if (!this.slides || !this.slides.length) {
      return;
    }

    this.currentIndex = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  goToSlide(index) {
    this.setActiveSlide(index);
    this.startAutorotate();
  }

  startAutorotate() {
    this.stopAutorotate();

    const shouldAutorotate = this.config.autorotate || theme.config.mqlSmall;

    if (!shouldAutorotate || !this.slides || this.slides.length <= 1) {
      return;
    }

    this.autorotateTimer = window.setInterval(() => {
      this.goToSlide(this.currentIndex + 1);
    }, this.config.autorotateSpeed);
  }

  stopAutorotate() {
    if (this.autorotateTimer) {
      window.clearInterval(this.autorotateTimer);
      this.autorotateTimer = null;
    }
  }

  initMarqueeOptimized() {
    setTimeout(() => {
      this.marqueeAnimation();
    });

    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isAnimated && this.classList.remove('marquee--paused');
        }
        else {
          this.isAnimated && this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);

    window.addEventListener('resize', this.onMarqueeResize, { passive: true });
  }

  marqueeAnimation() {
    const slider = this.querySelector('.announcement-slider');

    if (slider.scrollWidth > this.clientWidth) {
      if (!this.isAnimated) {
        for (let index = 0; index < 2; index++) {
          const clone = slider.cloneNode(true);
          clone.setAttribute('aria-hidden', true);
          this.appendChild(clone);
        }

        this.classList.add('marquee');
        this.style.setProperty('--duration', `${(slider.scrollWidth / this.config.space) * this.config.moveTime}s`);
        this.isAnimated = true;
      }
    }
    else if (this.isAnimated) {
      for (let index = 0; index < 2; index++) {
        this.removeChild(this.lastElementChild);
      }
      this.classList.remove('marquee');
      this.isAnimated = false;
    }
  }

  initMarquee() {
    setTimeout(() => {
      const slider = this.querySelector('.announcement-slider');

      for (let index = 0; index < 10; index++) {
        const clone = slider.cloneNode(true);
        clone.setAttribute('aria-hidden', true);
        this.appendChild(clone);
      }

      this.classList.add('marquee');
      this.style.setProperty('--duration', `${(slider.clientWidth / this.config.space) * this.config.moveTime}s`);
    });

    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.classList.remove('marquee--paused');
        }
        else {
          this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);
  }
}
customElements.define('announcement-bar', AnnouncementBar);
